// IA: modo, modelo (contrato v2: el director elige uno de MODELOS_IA; lo de aquí manda sobre IA_MODELO del .env), la
// cascada de la lectura (12-oct, apagada por omisión), llave (pegar o borrar; solo se ven los últimos 4 caracteres, nunca
// la llave), gasto del mes y tope.
import { useEffect, useState } from 'react';
import { Check, KeyRound, RotateCcw, Save, Trash2 } from 'lucide-react';
import { CASCADA_IA_POR_OMISION, IDS_MODELOS_IA, MODELO_IA, MODELO_IA_FUERTE, MODELOS_CASCADA, MODELOS_IA, numero } from '@cotizador/compartido';
import type { CascadaIA, EstadoIA, IdModeloIA } from '@cotizador/compartido';
import { api } from '../../api/consultas.ts';
import { usarSesion } from '../../sesion.tsx';
import { Boton, BotonConfirmar, CampoTexto, Cargando, Chip, ErrorEnLinea, Seccion, Segmentado } from '../../componentes/ui.tsx';
import { numeroDe } from '../../logica/cuestionario.ts';

/** De dónde sale el modelo vigente (EstadoIA.modelo_origen; un servidor de antes del contrato v2 no lo manda). */
const ORIGEN_MODELO: Record<NonNullable<EstadoIA['modelo_origen']>, string> = {
  omision: 'El de omisión (el que decidió Diego).',
  env: 'Fijado en el .env del servidor (IA_MODELO).',
  pantalla: 'Elegido aquí. Manda sobre el .env.',
};

const usd = (x: number) => numero(x, Number.isInteger(x) ? 0 : 2);
/** "USD 4 entrada · 20 salida, por millón de tokens" (y la tarifa larga de Haiku 5.5, si aplica). */
function precioCorto(id: IdModeloIA): string {
  const p = MODELOS_IA[id].precio;
  const largo = p.largo ? ` (con más de ${numero(p.largo.mas_de_tokens_entrada / 1000, 0)}K de entrada: ${usd(p.largo.entrada)} · ${usd(p.largo.salida)})` : '';
  return `USD ${usd(p.entrada)} entrada · ${usd(p.salida)} salida, por millón de tokens${largo}`;
}

type OpcionCascada = 'apagada' | (typeof MODELOS_CASCADA)[number];

const MODO: Record<EstadoIA['modo'], { texto: string; tono: 'verde' | 'ambar' | 'rojo' }> = {
  real: { texto: 'Conectada', tono: 'verde' },
  simulado: { texto: 'Simulada', tono: 'ambar' },
  sin_llave: { texto: 'Sin llave', tono: 'rojo' },
};

export function ConfigIA() {
  const { recargar } = usarSesion();
  const [estado, setEstado] = useState<EstadoIA | null>(null);
  const [llave, setLlave] = useState('');
  const [tope, setTope] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [errorModelo, setErrorModelo] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState<'llave' | 'borrar' | 'tope' | 'modelo' | 'omision' | null>(null);
  const [modelo, setModelo] = useState<IdModeloIA | null>(null);
  const [cascada, setCascada] = useState<OpcionCascada>('apagada');
  const [errorCascada, setErrorCascada] = useState<unknown>(null);
  const [guardandoCascada, setGuardandoCascada] = useState(false);
  const cascadaDe = (c: CascadaIA | undefined): OpcionCascada => (c?.activa ? c.modelo_barato : 'apagada');

  const cargar = async () => {
    try {
      const e = await api.estadoIA();
      setEstado(e);
      setModelo(e.modelo);
      setCascada(cascadaDe(e.cascada));
      setTope(e.tope_mes_usd === null ? '' : String(e.tope_mes_usd));
    } catch (x) {
      setError(x);
    }
  };

  /** Cambia el modelo (null = volver al de omisión). El error va junto al selector, no hasta abajo. */
  const cambiarModelo = async (que: 'modelo' | 'omision', id: IdModeloIA | null) => {
    setOcupado(que);
    setErrorModelo(null);
    try {
      const e = await api.guardarModeloIA(id);
      setEstado(e);
      setModelo(e.modelo);
      await recargar();
    } catch (x) {
      setErrorModelo(x);
    } finally {
      setOcupado(null);
    }
  };
  useEffect(() => {
    void cargar();
  }, []);

  const guardarCascada = async () => {
    setGuardandoCascada(true);
    setErrorCascada(null);
    try {
      const nueva: CascadaIA = cascada === 'apagada' ? { ...CASCADA_IA_POR_OMISION, ...(estado?.cascada ? { modelo_barato: estado.cascada.modelo_barato } : {}), activa: false } : { activa: true, modelo_barato: cascada };
      const e = await api.guardarCascadaIA(nueva);
      setEstado(e);
      setCascada(cascadaDe(e.cascada));
      await recargar();
    } catch (x) {
      setErrorCascada(x);
    } finally {
      setGuardandoCascada(false);
    }
  };

  const hacer = async (que: 'llave' | 'borrar' | 'tope', f: () => Promise<unknown>) => {
    setOcupado(que);
    setError(null);
    try {
      await f();
      await cargar();
      await recargar();
      if (que === 'llave') setLlave('');
    } catch (x) {
      setError(x);
    } finally {
      setOcupado(null);
    }
  };

  if (!estado) return error ? <ErrorEnLinea error={error} /> : <Cargando renglones={5} />;
  const m = MODO[estado.modo];
  const usoPct = estado.tope_mes_usd ? Math.min(100, (estado.gasto_mes_usd / estado.tope_mes_usd) * 100) : 0;
  const topeNum = numeroDe(tope);
  const topeValido = tope.trim() === '' || (topeNum !== null && Number.isFinite(topeNum) && topeNum > 0 && topeNum <= 10_000);

  return (
    <div className="pila pila--grande">
      <Seccion titulo="Inteligencia artificial" descripcion={`${MODELOS_IA[estado.modelo]?.nombre ?? estado.modelo} por la API de Anthropic. La llave vive solo en el servidor.`}>
        <div className="hoja pila">
          <div className="estado-ia">
            <Chip tono={m.tono}>{m.texto}</Chip>
            <span>{estado.mensaje}</span>
          </div>
          <dl className="resumen__lista">
            <div className="resumen__fila">
              <dt>Llave</dt>
              <dd>
                {estado.llave_final
                  ? `•••• ${estado.llave_final} (${estado.llave_origen === 'env' ? 'en .env' : estado.llave_origen === 'secret_manager' ? 'en Secret Manager' : 'pegada aquí'})`
                  : 'No hay llave'}
              </dd>
            </div>
            <div className="resumen__fila">
              <dt>Gasto de este mes</dt>
              <dd>
                {numero(estado.gasto_mes_usd, 2)} USD
                {estado.tope_mes_usd !== null && ` de ${numero(estado.tope_mes_usd, 0)} USD`}
              </dd>
            </div>
          </dl>
          {estado.tope_mes_usd !== null && (
            <div className="medidor-gasto" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(usoPct)} aria-label="Gasto del mes contra el tope">
              <span className={usoPct >= 90 ? 'es-alto' : ''} style={{ width: `${usoPct}%` }} />
            </div>
          )}
        </div>
      </Seccion>

 <Seccion
        titulo="Modelo"
        descripcion="Con qué modelo de Claude lee los recibos y revisa la rentabilidad. Cada uno cobra distinto: el tope de gasto se cuida con su precio."
      >
        <div className="hoja pila modelos">
          {/* Una columna en el celular (tres tarjetas lado a lado no caben en 390); tres en computadora (CSS). */}
          <Segmentado<IdModeloIA>
            etiqueta="Modelo de la IA"
            valor={modelo}
            alCambiar={setModelo}
            columnas={1}
            opciones={IDS_MODELOS_IA.map((id) => ({
              valor: id,
              texto: (
                <>
                  {MODELOS_IA[id].nombre}
                  {MODELOS_IA[id].recomendado && <span className="modelo__recomendado"> · recomendado</span>}
                </>
              ),
              detalle: (
                <>
                  {precioCorto(id)}
                  {id === estado.modelo && <span className="modelo__vigente"> · en uso</span>}
                  <br />
                  {MODELOS_IA[id].nota}
                </>
              ),
            }))}
            ayuda={estado.modelo_origen ? ORIGEN_MODELO[estado.modelo_origen] : undefined}
          />
          <ErrorEnLinea error={errorModelo} alCerrar={() => setErrorModelo(null)} />
          <div className="fila-botones fila-botones--entre">
            {estado.modelo_origen === 'pantalla' ? (
              <Boton chico variante="fantasma" icono={<RotateCcw aria-hidden />} cargando={ocupado === 'omision'} onClick={() => void cambiarModelo('omision', null)}>
                Volver al de omisión ({MODELOS_IA[MODELO_IA].nombre})
              </Boton>
            ) : (
              <span />
            )}
            <Boton
              variante="principal"
              icono={<Check aria-hidden />}
              cargando={ocupado === 'modelo'}
              disabled={modelo === null || modelo === estado.modelo}
              onClick={() => modelo && void cambiarModelo('modelo', modelo)}
            >
              {modelo && modelo !== estado.modelo ? `Usar ${MODELOS_IA[modelo].nombre}` : 'Usar este modelo'}
            </Boton>
          </div>
        </div>
      </Seccion>

      {estado.cascada && (
        <Seccion
          titulo="Cascada para leer fotos de recibos"
          descripcion={`Apagada por omisión. Las fotos y lo que el lector sin IA no entiende los lee primero un modelo barato; si las cuentas de un recibo no cuadran (kWh contra importes, periodos completos, tarifa contra demanda) o el modelo marca duda, ese recibo se vuelve a leer con ${MODELOS_IA[estado.modelo].nombre.startsWith('Claude Opus') ? MODELOS_IA[estado.modelo].nombre : MODELOS_IA[MODELO_IA_FUERTE].nombre}.`}
        >
          <div className="hoja pila">
            <Segmentado<OpcionCascada>
              etiqueta="Cascada"
              valor={cascada}
              alCambiar={setCascada}
              columnas={1}
              opciones={[
                { valor: 'apagada', texto: 'Apagada', detalle: `Todo lo lee ${MODELOS_IA[estado.modelo].nombre}.${cascadaDe(estado.cascada) === 'apagada' ? ' · en uso' : ''}` },
                ...MODELOS_CASCADA.map((id) => ({
                  valor: id,
                  texto: `${MODELOS_IA[id].nombre} primero`,
                  detalle: `${precioCorto(id)}. Lo que no cuadra, con Opus.${cascadaDe(estado.cascada) === id ? ' · en uso' : ''}`,
                })),
              ]}
            />
            <ErrorEnLinea error={errorCascada} alCerrar={() => setErrorCascada(null)} />
            <div className="fila-botones fila-botones--entre">
              <span />
              <Boton variante="principal" icono={<Check aria-hidden />} cargando={guardandoCascada} disabled={cascada === cascadaDe(estado.cascada)} onClick={() => void guardarCascada()}>
                {cascada === 'apagada' ? 'Apagar la cascada' : 'Encender la cascada'}
              </Boton>
            </div>
          </div>
        </Seccion>
      )}

      <Seccion
        titulo="Llave de Anthropic"
        descripcion={
          estado.llave_secret_manager
            ? 'En la nube la llave vive en Secret Manager de Google: aquí solo se ve cómo termina.'
            : 'Créala en console.anthropic.com con un tope de gasto mensual y pégala aquí. Después de guardarla ya no se vuelve a enseñar.'
        }
      >
        <div className="hoja pila">
          {estado.llave_secret_manager ? (
            <p className="nota-suave">
              La llave vive en Secret Manager (secreto anthropic-api-key), no en esta pantalla. Para cambiarla, Diego agrega una versión nueva del secreto en la
              consola de Google y se vuelve a desplegar el cotizador.
            </p>
          ) : estado.llave_origen === 'env' ? (
            <p className="nota-suave">La llave está en el archivo .env del servidor y esa manda. Para cambiarla, edita .env y reinicia el servidor.</p>
          ) : (
            <>
              <CampoTexto
                etiqueta="Pegar llave nueva"
                type="password"
                autoComplete="off"
                spellCheck={false}
                placeholder="sk-ant-…"
                value={llave}
                onChange={(e) => setLlave(e.target.value)}
                ayuda="Se manda una vez al servidor; aquí nunca se guarda ni se vuelve a ver."
              />
              <div className="fila-botones fila-botones--entre">
                {estado.llave_final ? (
                  <BotonConfirmar pregunta="¿Borrar la llave? La IA se apaga." cargando={ocupado === 'borrar'} icono={<Trash2 aria-hidden />} alConfirmar={() => void hacer('borrar', () => api.borrarLlaveIA())}>
                    Borrar llave
                  </BotonConfirmar>
                ) : (
                  <span />
                )}
                <Boton variante="principal" icono={<KeyRound aria-hidden />} cargando={ocupado === 'llave'} disabled={!llave.trim().startsWith('sk-ant-')} onClick={() => void hacer('llave', () => api.guardarLlaveIA(llave.trim()))}>
                  Guardar llave
                </Boton>
              </div>
            </>
          )}
        </div>
      </Seccion>

      <Seccion titulo="Tope de gasto del cotizador" descripcion="Además del tope de Anthropic. Al llegar, la IA se detiene con un aviso hasta el mes siguiente.">
        <div className="hoja fila-guardar-version">
          <CampoTexto etiqueta="Tope al mes" inputMode="decimal" sufijo="USD" value={tope} onChange={(e) => setTope(e.target.value)} error={topeValido ? null : 'Entre 1 y 10,000 USD, o vacío para sin tope.'} ayuda="Vacío = sin tope propio." />
          <Boton icono={<Save aria-hidden />} cargando={ocupado === 'tope'} disabled={!topeValido} onClick={() => void hacer('tope', () => api.guardarTopeIA(tope.trim() === '' ? null : topeNum))}>
            Guardar tope
          </Boton>
        </div>
      </Seccion>
      <ErrorEnLinea error={error} alCerrar={() => setError(null)} />
    </div>
  );
}
